import React, { useEffect, useState } from 'react';
import { useAppStore, useTeamData, useInvestmentData } from '@/stores/appStore';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { useProjectMetrics } from '@/hooks/useProjectMetrics';
import { DollarSign, Users, TrendingUp, Calendar, Target, BarChart3, Edit2, ArrowUpRight, Activity, Briefcase, MapPin, Globe } from 'lucide-react';

export default function ProjectOverview() {
  const state = useAppStore();
  const { teamRoles } = useTeamData();
  const { data: investmentData } = useInvestmentData();
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [editForm, setEditForm] = useState({
    projectName: '',
    description: '',
    industry: ''
  });

  // Sync project data to localStorage if startupId is present
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const startupId = urlParams.get('startupId');
    
    if (startupId) {
      const savedStartups = localStorage.getItem('userStartups');
      if (savedStartups) {
        try {
          const startups = JSON.parse(savedStartups);
          const startupIndex = startups.findIndex((s: any) => s.id === startupId);
          
          if (startupIndex >= 0) {
            startups[startupIndex] = {
              ...startups[startupIndex],
              name: state.projectName,
              description: state.description,
              updatedAt: new Date(),
              data: {
                ...startups[startupIndex].data,
                companyProfile: {
                  ...startups[startupIndex].data?.companyProfile,
                  name: state.projectName,
                  description: state.description,
                  industry: state.industry,
                }
              }
            };
            localStorage.setItem('userStartups', JSON.stringify(startups));
          }
        } catch (error) {
          console.error('Error saving project data:', error);
        }
      }
    }
  }, [state.projectName, state.description, state.industry]);

  const {
    breakEvenMonthValue,
    finalMetrics,
    companyValuation,
    roi,
    startupMetrics,
    formatCurrency
  } = useProjectMetrics();

  // Calculate team size
  const teamSize = teamRoles.reduce((total, role) => {
    const currentHeadcount = role.headcount[role.headcount.length - 1] || 0;
    return total + currentHeadcount;
  }, 0);

  const handleEditOpen = () => {
    setEditForm({
      projectName: state.projectName,
      description: state.description || '',
      industry: state.industry || ''
    });
    setIsEditOpen(true);
  };

  const handleSave = () => {
    state.setProjectData({
      projectName: editForm.projectName,
      description: editForm.description,
      industry: editForm.industry
    });
    setIsEditOpen(false);
  };

  const MetricCard = ({ 
    title, 
    value, 
    icon: Icon, 
    trend, 
    trendLabel,
    colorClass = "text-primary"
  }: { 
    title: string, 
    value: string | number, 
    icon: any, 
    trend?: string,
    trendLabel?: string,
    colorClass?: string
  }) => (
    <Card className="group relative overflow-hidden border-border/60 bg-card/50 hover:bg-card hover:border-border transition-all duration-300 shadow-sm hover:shadow-md">
      <CardContent className="p-6">
        <div className="flex items-center justify-between mb-4">
          <div className={`p-2 rounded-lg bg-background border border-border/50 group-hover:border-${colorClass.replace('text-', '')}/30 group-hover:bg-${colorClass.replace('text-', '')}/5 transition-colors duration-300`}>
            <Icon className={`w-5 h-5 ${colorClass}`} />
          </div>
          {trend && (
            <div className="flex items-center gap-1 text-[10px] font-medium text-emerald-600 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-950/40 px-2 py-1 rounded-full">
              <TrendingUp className="w-3 h-3" />
              {trend}
            </div>
          )}
        </div>
        
        <div className="space-y-1">
          <h3 className="text-xs font-medium text-muted-foreground uppercase tracking-wider">{title}</h3>
          <div className="flex items-baseline gap-2">
            <span className="text-2xl font-bold tracking-tight text-foreground">{value}</span>
          </div>
          {trendLabel && (
            <p className="text-xs text-muted-foreground/80 mt-1.5 flex items-center gap-1.5">
              <span className="inline-block w-1 h-1 rounded-full bg-border"></span>
              {trendLabel}
            </p>
          )}
        </div>
      </CardContent>
    </Card>
  );

  return (
    <div className="max-w-[1600px] mx-auto py-6 space-y-8 animate-in fade-in duration-500">
      {/* Header Section */}
      <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-6 pb-6 border-b border-border/40">
        <div className="space-y-4 max-w-3xl">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <div className="h-8 w-1 bg-gradient-to-b from-primary to-primary/50 rounded-full"></div>
              <h1 className="text-3xl font-bold tracking-tight text-foreground">{state.projectName}</h1>
            </div>
            
            <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground pl-4">
              <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-secondary/50 border border-border/50">
                <Briefcase className="w-3.5 h-3.5" />
                <span>{state.industry || 'Industry not set'}</span>
              </div>
              <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-secondary/50 border border-border/50">
                <Globe className="w-3.5 h-3.5" />
                <span>{state.country}</span>
              </div>
              <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-secondary/50 border border-border/50">
                <Calendar className="w-3.5 h-3.5" />
                <span>{state.planningMonths} Months Plan</span>
              </div>
            </div>
          </div>

          {state.description && (
            <p className="text-muted-foreground leading-relaxed pl-4 max-w-2xl">
              {state.description}
            </p>
          )}
        </div>

        <Button 
          onClick={handleEditOpen} 
          variant="outline" 
          className="group hover:border-primary/50 transition-colors"
        >
          <Edit2 className="w-4 h-4 mr-2 group-hover:text-primary transition-colors" />
          Edit Details
        </Button>
      </div>

      {/* Metrics Grid */}
      <div className="space-y-8">
        {/* Financials Group */}
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 px-1">
              <Activity className="w-4 h-4 text-primary" />
              <h2 className="text-sm font-semibold tracking-wide uppercase text-muted-foreground">Financial Overview</h2>
            </div>
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <MetricCard 
              title="Projected ROI" 
              value={`${roi.toFixed(1)}%`} 
              icon={TrendingUp}
              trend="+12%"
              trendLabel="Above market avg"
              colorClass="text-emerald-500"
            />
            <MetricCard 
              title="Company Valuation" 
              value={formatCurrency(companyValuation)} 
              icon={DollarSign}
              trend={`${investmentData.revenueMultiple}x`}
              trendLabel="Revenue Multiple"
              colorClass="text-purple-500"
            />
            <MetricCard 
              title="Break-even Point" 
              value={breakEvenMonthValue ? `Month ${breakEvenMonthValue}` : 'N/A'} 
              icon={Calendar} 
              colorClass="text-blue-500"
            />
            <MetricCard 
              title="Monthly Burn" 
              value={formatCurrency(startupMetrics.monthlyBurnRate)} 
              icon={Target}
              trendLabel="Avg. over projection"
              colorClass="text-red-500"
            />
          </div>
        </div>

        {/* Growth Group */}
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 px-1">
              <BarChart3 className="w-4 h-4 text-primary" />
              <h2 className="text-sm font-semibold tracking-wide uppercase text-muted-foreground">Growth & Operations</h2>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <MetricCard 
              title="Total Users" 
              value={finalMetrics ? finalMetrics.users.toLocaleString() : '0'} 
              icon={Users}
              trend={`${investmentData.monthlyGrowthRate}%`}
              trendLabel="Monthly Growth"
              colorClass="text-indigo-500"
            />
            <MetricCard 
              title="Monthly Revenue" 
              value={formatCurrency(startupMetrics.mrr)} 
              icon={BarChart3} 
              colorClass="text-cyan-500"
            />
            <MetricCard 
              title="Annual Revenue" 
              value={formatCurrency(startupMetrics.arr)} 
              icon={ArrowUpRight} 
              colorClass="text-teal-500"
            />
            <MetricCard 
              title="Team Size" 
              value={teamSize} 
              icon={Users}
              trendLabel="Full-time employees"
              colorClass="text-orange-500"
            />
          </div>
        </div>
      </div>

      {/* Edit Dialog */}
      <Dialog open={isEditOpen} onOpenChange={setIsEditOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Edit Project Details</DialogTitle>
            <DialogDescription>
              Update your project's core information.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-6 py-4">
            <div className="space-y-2">
              <Label htmlFor="edit-name">Project Name</Label>
              <Input
                id="edit-name"
                value={editForm.projectName}
                onChange={(e) => setEditForm({ ...editForm, projectName: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-industry">Industry</Label>
              <Input
                id="edit-industry"
                value={editForm.industry}
                onChange={(e) => setEditForm({ ...editForm, industry: e.target.value })}
                placeholder="e.g. SaaS, E-commerce, Fintech"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-description">Description</Label>
              <Textarea
                id="edit-description"
                value={editForm.description}
                onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                rows={4}
                className="resize-none"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsEditOpen(false)}>Cancel</Button>
            <Button onClick={handleSave}>Save Changes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
